import Cookies from "js-cookie";
import { extractDomain } from "./utils/domain.js";

declare global {
  interface Window {
    endorsely_referral: string | null;
    endorsely: EndorselyProvider;
  }
}

const API_URL = "https://dev.endorsely.com/api/public";

const endorsely_domain = extractDomain(window.location.hostname);

// Get UTM referral parameters (prioritize "ref", then "aff", then "via")
const endorsely_params = new URLSearchParams(window.location.search);
const endorsely_referral_param_key =
  endorsely_params.get("ref") ||
  endorsely_params.get("aff") ||
  endorsely_params.get("via") ||
  endorsely_params.get("_by") ||
  endorsely_params.get("by") ||
  endorsely_params.get("utm_source") ||
  null;
const endorsely_referral_param_value =
  endorsely_params.get("aff") ||
  endorsely_params.get("ref") ||
  endorsely_params.get("via") ||
  endorsely_params.get("_by") ||
  endorsely_params.get("by") ||
  endorsely_params.get("utm_source") ||
  null;

const endorsely_script = document.querySelector("script[data-endorsely]");
const endorsely_organization_id = endorsely_script
  ? endorsely_script.getAttribute("data-endorsely")
  : null;

// When setting cookies via js-cookie, an "expires" value passed as a number
// is converted into an expiration date (in days).
// Adjust secure options based on your environment.
const isSecure = window.location.protocol === "https:";

const isLocal =
  window.location.hostname === "localhost" ||
  window.location.hostname === "127.0.0.1";

const endorsely_set_cookie = (referral: string, duration: number) => {
  Cookies.set("endorsely_referral", referral, {
    expires: duration, // number of days until expiration
    domain: endorsely_domain, // Ensure this domain matches your current domain!
    path: "/",
    sameSite: isLocal ? "Lax" : "None",
    secure: isSecure, // Only use true in secure (HTTPS) contexts.
  });
};

const endorsely_get_cookie = (): string | "" => {
  const value = Cookies.get("endorsely_referral");
  return value !== undefined && value !== "null" ? value : "";
};

const endorsely_remove_cookie = () => {
  Cookies.remove("endorsely_referral", {
    domain: endorsely_domain,
    path: "/",
  });
};

const endorsely_referral_saved =
  endorsely_get_cookie() && endorsely_get_cookie() !== "null"
    ? endorsely_get_cookie()
    : null;

class EndorselyProvider {
  async create() {
    const organizationId = endorsely_organization_id;
    const code = endorsely_referral_param_value;

    // Get all URL parameters and convert them to an object for subids
    const subids: Record<string, string> = {};
    endorsely_params.forEach((value, key) => {
      // Skip common affiliate parameters
      if (
        value &&
        !["ref", "via", "aff", "_by", "by"].some((param) => param === key)
      ) {
        subids[key] = value;
      }
    });

    const payload = {
      organizationId,
      code,
      subids,
      path: window.location.pathname,
    };

    if (
      !endorsely_organization_id ||
      !endorsely_domain ||
      !endorsely_referral_param_key
    ) {
      return;
    }

    try {
      const resp = await fetch(`${API_URL}/track`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(payload),
      });
      const affiliateInfo = await resp.json();
      if (affiliateInfo.referral) {
        window.endorsely_referral = affiliateInfo.referral;
        endorsely_set_cookie(affiliateInfo.referral, affiliateInfo.duration);
      } else {
        endorsely_remove_cookie();
        window.endorsely_referral = null;
      }
      return affiliateInfo.referral;
    } catch (error) {
      endorsely_remove_cookie();
      window.endorsely_referral = null;
      return "error";
    }
  }
}

const endorsely = new EndorselyProvider();
window.endorsely = endorsely;

if (
  endorsely_referral_param_key &&
  endorsely_referral_param_value &&
  !endorsely_referral_saved
) {
  endorsely.create();
} else if (endorsely_referral_saved) {
  window.endorsely_referral = endorsely_referral_saved;
} else {
  window.endorsely_referral = null;
}
